import { styled } from "goober";
import type {
  ChatEntryWithSubTables,
  UserAction as UserActionData,
} from "../../../shared/aiSdkMessages";
import { pluralize } from "../../lib/inflection";
import { userDisplayName } from "../../lib/userDisplayName";
import { Icon, type IconName } from "../Icon";
import { Noodle } from "../Noodle";
import { Text } from "../Text";
import { DefaultIcon, HoverIcon, ToggleIconContainer } from "../ToggleIcon";
import { ExpandWrapper } from "./Expand";

const Container = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const GroupHeader = styled("div")`
  display: flex;
  align-items: center;
  height: 28px;
  gap: 4px;
  padding: 6px 10px 6px 8px;
  border-radius: 9999px;
  width: fit-content;
  cursor: pointer;
  &:hover {
    background-color: var(--alpha4);
    ${DefaultIcon} {
      opacity: 0;
      transform: rotate(90deg) scale(0.5);
    }
    ${HoverIcon} {
      opacity: 1;
      transform: rotate(0deg) scale(1);
    }
  }
`;

const GroupHeaderInner = styled("div")`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ActionsContainer = styled("div")`
  display: flex;
  flex-direction: column;
  margin-top: 8px;
`;

const ActionItem = styled("div")`
  display: flex;
  flex-direction: row;
  gap: 2px;
  align-items: stretch;
  margin-left: 12px;
`;

const NoodleColumn = styled("div")`
  width: 12px;
  align-self: stretch;
  display: flex;
`;

const ActionInner = styled("div")`
  display: flex;
  align-items: center;
  height: 28px;
  gap: 4px;
  padding: 6px 10px 6px 8px;
`;

const GroupExpandInner = styled("div")`
  overflow: hidden;
  min-height: 0;
  content-visibility: hidden;
  &[data-expanded="true"] {
    overflow-y: visible;
    content-visibility: visible;
  }
`;

export function getActionDisplay(userAction: UserActionData): {
  icon: IconName;
  iconColor?: string;
  text: string;
} {
  switch (userAction.actionType) {
    case "pushDraftToLive":
      return {
        icon: "rocket",
        text: `Pushed ${userAction.metadata.draftName} live`,
      };
    case "updateStepColor":
      return { icon: "palette", text: "Changed step color" };
    case "associateStepWithConnectedApp":
      return {
        icon: "check-filled",
        iconColor: "var(--green-600)",
        text: `Connected ${userAction.metadata.connectedAppName} to ${userAction.metadata.stepDir}`,
      };
    case "dissociateStepFromConnectedApp":
      return {
        icon: "circle-minus",
        text: `Disconnected ${userAction.metadata.connectedAppName} from ${userAction.metadata.stepDir}`,
      };
    case "deleteStickyNote":
      return {
        icon: "trash",
        text: `Deleted note "${userAction.metadata.title}"`,
      };
    case "updateStepTimeout":
      return {
        icon: "clock",
        text: userAction.metadata.timeout
          ? `Set timeout of ${userAction.metadata.stepDir} to ${userAction.metadata.timeout}s`
          : `Removed timeout from ${userAction.metadata.stepDir}`,
      };
    case "reorderStickyNotes":
      return { icon: "arrow-up-down", text: "Reordered notes" };
    case "pinStepTest":
      return {
        icon: "pin",
        text: `Pinned step test "${userAction.metadata.name}" on ${userAction.metadata.stepDir}`,
      };
    case "unpinStepTest":
      return {
        icon: "pin",
        text: `Unpinned step test "${userAction.metadata.name}" on ${userAction.metadata.stepDir}`,
      };
  }
}

type Props = {
  entries: ChatEntryWithSubTables[];
  collapsed: boolean;
  toggleCollapsed: () => void;
};

export function UserActionGroup({
  entries,
  collapsed,
  toggleCollapsed,
}: Props) {
  const firstUser = entries[0]?.userAction?.user;
  const userName = firstUser ? userDisplayName(firstUser) : "Someone";
  const count = entries.length;

  return (
    <Container>
      <GroupHeader onClick={toggleCollapsed}>
        <GroupHeaderInner>
          <ToggleIconContainer aria-hidden>
            <DefaultIcon>
              <Icon
                name="mouse-pointer-2"
                size={12}
                color="var(--text-secondary)"
              />
            </DefaultIcon>
            <HoverIcon>
              <Icon
                name={collapsed ? "plus" : "minus"}
                size={12}
                color="var(--text-secondary)"
              />
            </HoverIcon>
          </ToggleIconContainer>
          <Text variant="text-small" color="var(--text-secondary)">
            <Text variant="text-small-md" color="var(--text-primary)">
              {userName}
            </Text>{" "}
            made {count} {pluralize("change", count)}
          </Text>
        </GroupHeaderInner>
      </GroupHeader>
      <ExpandWrapper data-expanded={!collapsed}>
        <GroupExpandInner data-expanded={!collapsed}>
          <ActionsContainer>
            {entries.map((entry, index) => {
              const userAction = entry.userAction;
              if (!userAction) return null;
              const { icon, iconColor, text } = getActionDisplay(userAction);
              const isLast = index === entries.length - 1;

              return (
                <ActionItem key={entry.id}>
                  <NoodleColumn>
                    <Noodle
                      gridSize={7}
                      variant={isLast ? "terminate" : "branch"}
                    />
                  </NoodleColumn>
                  <ActionInner>
                    <Icon
                      name={icon}
                      size={12}
                      color={iconColor ?? "var(--text-secondary)"}
                    />
                    <Text variant="text-small" color="var(--text-secondary)">
                      {text}
                    </Text>
                  </ActionInner>
                </ActionItem>
              );
            })}
          </ActionsContainer>
        </GroupExpandInner>
      </ExpandWrapper>
    </Container>
  );
}
